import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { selectLeftPanelVisibility } from 'common/components/leftPanel/store/leftPanelSlice'
import PageContainer from 'common/components/PageContainer'
import { useAppSelector } from 'common/hooks/redux'

import useGetMetabaseDashboardUrlQuery from '../../api/queries/useGetMetabaseDashboardUrlQuery'

import LeftSection from './LeftSection'
import { EMBED_CLASS_NAME } from './styles'

type Props = {
  dashboardId: string | undefined
  additionalParams?: Record<string, string>
}

export const MetabaseDashboard = ({ dashboardId, additionalParams }: Props) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const flags = useFlags()
  const leftPanelVisibility = useAppSelector(selectLeftPanelVisibility)

  const {
    data: embeddedUrl,
    isLoading,
    error,
  } = useGetMetabaseDashboardUrlQuery(dashboardId, accountId)

  if (!dashboardId) {
    return (
      <PageContainer className="w-full flex-grow flex flex-col xl:max-w-none">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-base text-red-600">
              Dashboard configuration error: Dashboard ID is not defined
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Please check your environment configuration.
            </p>
          </div>
        </div>
      </PageContainer>
    )
  }

  const buildUrl = () => {
    if (!embeddedUrl) {
      return undefined
    }

    if (!additionalParams || Object.keys(additionalParams).length === 0) {
      return embeddedUrl
    }

    const [baseUrl, hashFragment] = embeddedUrl.split('#')

    const params = new URLSearchParams()

    Object.entries(additionalParams).forEach(([key, value]) => {
      params.append(key, value)
    })

    const separator = baseUrl.includes('?') ? '&' : '?'
    const urlWithParams = `${baseUrl}${separator}${params.toString()}`

    return hashFragment ? `${urlWithParams}#${hashFragment}` : urlWithParams
  }

  const styledUrl = buildUrl()

  return (
    <div
      className={cx(
        'relative overflow-y-auto overflow-x-hidden h-full flex w-full',
        {
          'ml-30': leftPanelVisibility === 'hidden',
        }
      )}
    >
      {flags.showSavedReports && <LeftSection />}
      <PageContainer className="w-full flex-grow flex flex-col xl:max-w-none">
        {isLoading && <div>Loading dashboard...</div>}
        {error && <div>Failed to load dashboard</div>}
        {styledUrl && (
          <div
            style={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#FFFDF9',
            }}
          >
            <iframe
              src={styledUrl}
              className={EMBED_CLASS_NAME}
              title="Metabase Dashboard"
              allow="fullscreen"
              sandbox="allow-scripts allow-same-origin allow-forms"
              referrerPolicy="no-referrer"
            />
          </div>
        )}
      </PageContainer>
    </div>
  )
}
